import { getAnchorId, getComponentMargin, getComponentOutlineMargin } from '@/utils/builderUtils';
import Title from '@/components/Title/Title';
import beTwMerge from '@/utils/beTwMerge';
import Image from 'next/image';
import { useState } from 'react';
import useMediaQuery from '@/hooks/useMediaQuery';
import { ITitle } from '../CommonFields';

type ShopByShapeProps = {
  anchorLinkID: string;
  diamondItems?: {
    alt: string;
    img: string;
    url: string;
    title: string;
  }[];
  header?: ITitle;
  margin: boolean;
  outlineMargin: boolean;
  ringItems?: {
    alt: string;
    img: string;
    link?: string;
    title: string;
  }[];
  textPosition: string;
};
export default function ShopByShape({
  anchorLinkID,
  diamondItems = [],
  header,
  margin,
  outlineMargin,
  ringItems = [],
  textPosition,
}: ShopByShapeProps) {
  const [currentShape, setCurrentShapeShape] = useState<string>('');
  const [hoverShape, setHoverShape] = useState<string>('');
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const itemsNumber = diamondItems.length;
  const onClick = (href?: string, shape?: string) => {
    if (shape) {
      setHoverShape(shape);
    }
    if (href) {
      window.location.href = href;
    }
  };
  const onMouseEnter = (shape: string) => {
    setCurrentShapeShape(shape);
    setHoverShape(shape);
  };

  const onMouseLeave = () => {
    setCurrentShapeShape(diamondItems[0]?.title || '');
    setHoverShape('');
  };

  const renderDiamond = (sliceStart: number, sliceEnd: number) =>
    diamondItems.slice(sliceStart, sliceEnd).map(item => {
      const { alt, img, title, url } = item;
      const isActive = title === hoverShape;
      return (
        <div
          className="tw-flex tw-flex-col tw-items-center tw-gap-[8px] tw-pb-[6px]"
          key={title}
          onClick={() => {
            onClick(url);
          }}
          onMouseEnter={() => {
            onMouseEnter(title);
          }}
          onMouseLeave={onMouseLeave}
          role="button"
          style={{
            borderBottom: `2px solid ${isActive ? '#0d6363' : '#f9f9f9'}`,
            cursor: url ? 'pointer' : 'default',
          }}
          tabIndex={0}
        >
          <div className="tw-flex tw-justify-center tw-mix-blend-multiply">
            <Image alt={alt || title || ''} height={100} loading="lazy" src={img} width={100} />
          </div>
          <p
            className="tw-font-book tw-text-[16px] tw-m-0"
            style={{
              color: isActive ? '#0d6363' : '#2c2c2c',
            }}
          >
            {title}
          </p>
        </div>
      );
    });

  const renderRing = () => {
    if (!isDesktop || ringItems.length === 0) return null;
    const displayRing = ringItems.find(item => item.title === currentShape) || ringItems[0];

    const { alt, img, link, title } = displayRing;

    return (
      <div>
        <a href={link || '#'} className="tw-block tw-w-full tw-object-cover">
          <Image alt={alt || title || ''} className="!tw-relative tw-max-h-[226px]" fill loading="lazy" src={img} />{' '}
        </a>
      </div>
    );
  };

  return (
    <div
      className={beTwMerge('tw-bg-[#F9F9F9]', getComponentOutlineMargin(outlineMargin), getComponentMargin(margin))}
      {...getAnchorId(anchorLinkID)}
    >
      <div className="tw-w-full tw-bg-[#F9F9F9] tw-pt-[24px] tw-max-w-[1344px] tw-mx-auto tw-px-[16px] tw-pb-[32px] large:tw-px-[32px] large:tw-py-[80px]">
        <div className="shop-by-shape tw-w-full tw-flex tw-flex-col tw-items-center large:tw-flex-row">
          <div className="tw-flex tw-items-center tw-w-full large:tw-w-auto large:tw-min-w-[385px] tw-h-auto tw-flex-col tw-gap-[16px] large:tw-gap-0">
            {header?.content && (
              <Title
                component={header.titleHTMLTag}
                size={header.titleSize}
                weight={header.titleWeight}
                className={beTwMerge(
                  '!tw-mb-0 tw-w-full tw-text-center',
                  textPosition === 'left' && 'tw-text-left',
                  textPosition === 'right' && 'tw-text-right'
                )}
              >
                {header.content}
              </Title>
            )}
            {ringItems && renderRing()}
          </div>
          {isDesktop ? (
            <div className="tw-w-full">
              <div className="tw-flex tw-flex-col tw-items-start tw-gap-[16px] tw-capitalize">
                <div className="tw-flex tw-self-stretch tw-items-center tw-justify-between">
                  {renderDiamond(0, Math.ceil(itemsNumber / 2))}
                </div>
                <div className="tw-flex tw-self-stretch tw-items-center tw-justify-between">
                  {renderDiamond(Math.ceil(itemsNumber / 2), itemsNumber)}
                </div>
              </div>
            </div>
          ) : (
            <div className="tw-w-full">
              <ul
                className="tw-flex tw-pl-0 tw-mb-0 tw-items-start tw-gap-[8px] tw-overflow-x-scroll tw-list-none tw-list-image-none"
                style={{
                  overflowX: 'scroll',
                  overscrollBehaviorInline: 'contain',
                  scrollSnapType: 'x proximity',
                  scrollbarWidth: 'none',
                  mixBlendMode: 'multiply',
                  touchAction: 'pan-x',
                }}
              >
                {diamondItems.map(item => {
                  const { alt, img, url, title } = item;
                  const isActive = title === hoverShape;
                  return (
                    <li
                      className="tw-flex tw-flex-col tw-items-start tw-gap-[8px]"
                      key={title}
                      onClick={() => {
                        onClick(url, title);
                      }}
                    >
                      <Image alt={alt || title || ''} height={100} loading="lazy" src={img} width={100} />
                      <p
                        className="tw-w-full tw-font-book tw-text-[16px] tw-text-be-dark-gray tw-m-0 tw-text-center tw-capitalize tw-pb-[6px]"
                        style={{
                          borderBottom: `2px solid ${isActive ? '#0d6363' : '#F9F9F900'}`,
                          color: isActive ? '#0d6363' : '#2c2c2c',
                        }}
                      >
                        {title}
                      </p>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
